import React, { useState } from "react";
import { Plus, MoreVertical, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import AddRoleView from "../pages/Users/modal/AddRole";
import {
    useGetAllRolesQuery,
    useDeleteRoleByIdMutation,
} from "../../redux/services/settingsRoleApi";
import { usePermission } from "../../hooks/usePermission";

interface Role {
    id: string;
    name: string;
    description: string;
    users_count?: number;
    company_id: string | null;
}

interface RoleManagementProps {
    type: "company" | "individual";
}

const RoleManagement: React.FC<RoleManagementProps> = ({ type }) => {
    const { data: rolesData, isLoading: loadingRoles, refetch: refetchRoles } =
        useGetAllRolesQuery();

    const [deleteRole] = useDeleteRoleByIdMutation();
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [showAddRole, setShowAddRole] = useState(false);
    const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
    const [roleDesc, setRoleDesc] = useState<string | null>(null);
    const { hasPermission } = usePermission();

    const handleDeleteRole = async (id: string) => {
        if (!hasPermission("delete role")) {
            toast.error("You don't have permission to delete roles");
            return;
        }
        try {
            await deleteRole(id).unwrap();
            toast.success("Role deleted successfully");
            refetchRoles();
        } catch (err: any) {
            const message =
                err?.data?.message || err?.message || "Failed to delete role. Please try again.";
            toast.error(message);
        }
    };

    const [editingRoleIsDefault, setEditingRoleIsDefault] = useState(false);

    const handleEditClick = (role: Role) => {
        setRoleDesc(role.description);
        setEditingRoleId(role.id);
        setEditingRoleIsDefault(role.company_id === null);
        setShowAddRole(true);
        setOpenMenuId(null);
    };

    const rolesList = rolesData?.data?.data?.data || [];

    return (
        <div>
            {showAddRole ? (
                <div>
                    <button
                        onClick={() => {
                            setShowAddRole(false);
                            setEditingRoleId(null);
                        }}
                        className="flex items-center gap-2 text-[#143887] mb-6"
                    >
                        <ArrowLeft size={18} /> Back
                    </button>

                    <AddRoleView
                        roleDescription={roleDesc}
                        roleToEditId={editingRoleId}
                        isDefaultRole={editingRoleIsDefault}
                        onSaved={() => {
                            toast.success("Role saved successfully");
                            refetchRoles();
                            setShowAddRole(false);
                            setEditingRoleId(null);
                        }}
                        onClose={() => {
                            setShowAddRole(false);
                            setEditingRoleId(null);
                        }}
                    />
                </div>
            ) : (
                <>
                    <div className="flex justify-end gap-2 items-center pt-6 pb-5 mb-4 lg:pt-0 lg:pb-0">
                        <button
                            onClick={() => {
                                setEditingRoleId(null);
                                setShowAddRole(true);
                            }}
                            disabled={!hasPermission("create role")}
                            title={!hasPermission("create role") ? "You don't have permission to create roles" : undefined}
                            className={`flex items-center gap-2 px-4 py-2 rounded ${
                                hasPermission("create role")
                                    ? "bg-[#143887] text-white"
                                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                            }`}
                        >
                            <Plus size={16} /> Add new role
                        </button>
                    </div>

                    <div className="bg-[#DFE3EF] p-5 rounded-lg mb-6 h-[420px] overflow-y-auto thin-scrollbar">
                        {loadingRoles ? (
                            <div className="grid md:grid-cols-3 gap-4">
                                {Array.from({ length: 6 }).map((_, i) => (
                                    <div key={i} className="bg-white border rounded-lg p-4 shadow-sm animate-pulse h-24" />
                                ))}
                            </div>
                        ) : rolesList.length > 0 ? (
                            <div className="grid md:grid-cols-3 gap-4">
                                <AnimatePresence>
                                {rolesList.map((role: Role, idx: number) => {
                                    const isDefault = role.company_id === null;
                                    return (
                                    <motion.div
                                        key={role.id}
                                        initial={{ opacity: 0, y: 16 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, scale: 0.95 }}
                                        transition={{ duration: 0.22, delay: idx * 0.04 }}
                                        whileHover={{ scale: 1.02, boxShadow: "0 4px 16px rgba(20,56,135,0.10)" }}
                                        className="bg-white border rounded-lg p-4 shadow-sm relative"
                                    >
                                        <div className="flex justify-between items-start relative">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <h3 className="text-[14px] font-semibold text-[#143887]">
                                                        {role.name}
                                                    </h3>
                                                    {isDefault && (
                                                        <span className="text-[10px] bg-[#EEF2FF] text-[#143887] border border-[#143887]/20 px-2 py-0.5 rounded-full font-medium">
                                                            Default
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-[13px] text-[#143887] mt-0.5">
                                                    {role.description}
                                                </p>
                                                <p className="text-[12px] text-gray-500 mt-2">
                                                    {role.users_count ?? 0} {role.users_count === 1 ? "User" : "Users"}
                                                </p>
                                            </div>

                                            <div className="relative">
                                                <button
                                                    onClick={() =>
                                                        setOpenMenuId(openMenuId === role.id ? null : role.id)
                                                    }
                                                >
                                                    <MoreVertical size={16} className="text-gray-400" />
                                                </button>

                                                <AnimatePresence>
                                                {openMenuId === role.id && (
                                                    <motion.div
                                                        initial={{ opacity: 0, scale: 0.95, y: -4 }}
                                                        animate={{ opacity: 1, scale: 1, y: 0 }}
                                                        exit={{ opacity: 0, scale: 0.95, y: -4 }}
                                                        transition={{ duration: 0.15 }}
                                                        className="absolute right-0 mt-2 w-32 bg-white border rounded shadow-md z-10"
                                                    >
                                                        <button
                                                            onClick={hasPermission("update role") ? () => handleEditClick(role) : undefined}
                                                            disabled={!hasPermission("update role")}
                                                            title={!hasPermission("update role") ? "You don't have permission to update roles" : undefined}
                                                            className={`block w-full text-left px-4 py-2 text-sm ${
                                                                hasPermission("update role")
                                                                    ? "hover:bg-gray-100"
                                                                    : "text-gray-300 cursor-not-allowed"
                                                            }`}
                                                        >
                                                            Edit
                                                        </button>
                                                        {/* Delete — shown for all roles; disabled/grayed out for default (system) roles or missing permission */}
                                                        <button
                                                            onClick={!isDefault && hasPermission("delete role") ? () => handleDeleteRole(role.id) : undefined}
                                                            disabled={isDefault || !hasPermission("delete role")}
                                                            title={
                                                                isDefault
                                                                    ? "Default roles cannot be deleted"
                                                                    : !hasPermission("delete role")
                                                                    ? "You don't have permission to delete roles"
                                                                    : undefined
                                                            }
                                                            className={`block w-full text-left px-4 py-2 text-sm ${
                                                                isDefault || !hasPermission("delete role")
                                                                    ? "text-gray-300 cursor-not-allowed"
                                                                    : "text-red-600 hover:bg-red-50"
                                                            }`}
                                                        >
                                                            Delete
                                                        </button>
                                                    </motion.div>
                                                )}
                                                </AnimatePresence>
                                            </div>
                                        </div>
                                    </motion.div>
                                    );
                                })}
                                </AnimatePresence>
                            </div>
                        ) : (
                            <p className="text-sm text-gray-500">No roles found.</p>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default RoleManagement;
